/**
 * A "⋯" button that opens the less frequent actions of a page, so the head keeps one primary
 * action and a few secondary ones in sight:
 *
 *     <plane-menu label="More actions">
 *       <button @click=…>Rename</button>
 *       <a href=…>Make a template</a>
 *     </plane-menu>
 *
 * The items are the light DOM children, styled by formStyles (`plane-menu > button`), with their
 * handlers on the page. The menu closes when an item is chosen, on Escape and on a click elsewhere;
 * the arrow keys move between the items. It opens under the button, kept inside the window.
 */
import { LitElement, html } from 'lit';

import { formStyles } from '../../styles/shared/forms.js';
import { planeMenuStyles } from '../../styles/components/plane-menu.js';

/** Space kept between the open menu and the edge of the window, in pixels. */
const EDGE = 16;

class PlaneMenu extends LitElement {
  static properties = {
    label: { type: String },
    open: { type: Boolean, reflect: true },
  };

  static styles = [
    formStyles,
    planeMenuStyles,
  ];

  constructor() {
    super();
    this.label = 'More actions';
    this.open = false;
    this.onOutside = (event) => {
      if (!event.composedPath().includes(this)) {
        this.open = false;
      }
    };
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutside);
    super.disconnectedCallback();
  }

  /** The items that can be chosen now. */
  get items() {
    return [...this.children].filter((item) => !item.disabled && !item.hidden);
  }

  updated(changed) {
    if (!changed.has('open')) {
      return;
    }
    const panel = this.renderRoot.querySelector('.panel');
    if (this.open) {
      document.addEventListener('click', this.onOutside);
      // back inside the window on either side
      panel.style.transform = '';
      const rect = panel.getBoundingClientRect();
      const shift = rect.left < EDGE ? EDGE - rect.left : Math.min(0, window.innerWidth - EDGE - rect.right);
      if (shift !== 0) {
        panel.style.transform = `translateX(${shift}px)`;
      }
      this.items[0]?.focus();
    } else {
      document.removeEventListener('click', this.onOutside);
    }
  }

  onKeydown(event) {
    if (!this.open) {
      return;
    }
    if (event.key === 'Escape') {
      this.open = false;
      this.renderRoot.querySelector('button').focus();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const items = this.items;
      // the items live in the page's tree: its root knows which one has the focus
      const at = items.indexOf(this.getRootNode().activeElement);
      const next = event.key === 'ArrowDown' ? at + 1 : at - 1;
      items[(next + items.length) % items.length]?.focus();
    }
  }

  /** A chosen item closes the menu; its own handler does the rest. */
  onChoose(event) {
    if (event.target !== event.currentTarget) {
      this.open = false;
    }
  }

  /** The items are menu items to assistive technologies, whatever element they are. */
  onSlotChange() {
    for (const item of this.children) {
      item.setAttribute('role', 'menuitem');
    }
  }

  render() {
    return html`
      <span class="anchor" @keydown=${(event) => this.onKeydown(event)}>
        <button
          type="button"
          class="toggle"
          aria-haspopup="menu"
          aria-expanded=${this.open ? 'true' : 'false'}
          aria-controls="panel"
          aria-label=${this.label}
          title=${this.label}
          @click=${() => (this.open = !this.open)}
        >⋯</button>
        <div class="panel" id="panel" role="menu" aria-label=${this.label} ?hidden=${!this.open} @click=${(event) => this.onChoose(event)}>
          <slot @slotchange=${() => this.onSlotChange()}></slot>
        </div>
      </span>
    `;
  }
}

customElements.define('plane-menu', PlaneMenu);
